import React, { useState, useEffect } from 'react';
import { getBookings } from '../services/api';
import { FaEye } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';
import { SunIcon, MoonIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

const HandleBookings = () => {
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [filters, setFilters] = useState({
    courseTitle: '',
    license: '',
    timeSlot: [],
    isTrainerLive: '',
  });
  const [sortField, setSortField] = useState('createdAt'); // 'createdAt' | 'startDate' | 'endDate'
  const [sortOrder, setSortOrder] = useState('desc');
  const [isTimeSlotDropdownOpen, setIsTimeSlotDropdownOpen] = useState(false);
  const timeSlotRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const data = await getBookings();
        const all = Array.isArray(data) ? data : data.documents || [];
        setAllBookings(all);
      } catch (err) {
        console.error('Error fetching bookings:', err);
      }
    };

    fetchBookings();
  }, []);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = [...allBookings];

      if (filters.courseTitle)
        filtered = filtered.filter((b) => b.courseTitle === filters.courseTitle);
      // License filtering (null-safe)
      if (filters.license === 'Required') {
        filtered = filtered.filter((b) => b.license === 'Required');
      } else if (filters.license === 'Not Required') {
        filtered = filtered.filter((b) => !b.license);
      }
     if (filters.timeSlot.length > 0)
       filtered = filtered.filter(b => filters.timeSlot.includes(b.timeSlot));
      if (filters.isTrainerLive)
        filtered = filtered.filter((b) => String(b.isTrainerLive) === filters.isTrainerLive);

      if (search.trim()) {
        const s = search.toLowerCase();
        filtered = filtered.filter(b =>
          b.studentName?.toLowerCase().includes(s) ||
          b.bookingId?.toLowerCase().includes(s) ||
          b.carId?.toLowerCase().includes(s) ||
          b.studentId?.toLowerCase().includes(s) ||
          b.schoolOwnerId?.toLowerCase().includes(s) ||
          b.address.pincode?.includes(s)
        );
      }

      // Sort
      filtered.sort((a, b) => {
        const dateA = a[sortField]?.seconds || 0;
        const dateB = b[sortField]?.seconds || 0;
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });

      const start = currentPage * 10;
      const paginated = filtered.slice(start, start + 10);

      setBookings(paginated);
      setPageCount(Math.ceil(filtered.length / 10));
    };

    applyFilters();
  }, [allBookings, filters, sortField, sortOrder, search, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const idFromUrl = params.get('bookingId');
    const licenseFromUrl = params.get('license'); // New param

    if (idFromUrl) {
      setSearch(idFromUrl);
    }

    if (licenseFromUrl) {
      setFilters(prev => ({
        ...prev,
        license: licenseFromUrl
      }));
    }
  }, [location.search]);


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (timeSlotRef.current && !timeSlotRef.current.contains(e.target)) {
        setIsTimeSlotDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const AM_SLOTS = [
    '06:00 AM', '06:30 AM', '07:00 AM', '07:30 AM',
    '08:00 AM', '08:30 AM', '09:00 AM', '09:30 AM',
    '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  ];

  const PM_SLOTS = [
    '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
    '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
    '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM', '08:00 PM'
  ];

  const handleTimeSlotToggle = (slot) => {
    setFilters(prev => {
      const newSlots = prev.timeSlot ? [...prev.timeSlot] : [];
      const index = newSlots.indexOf(slot);
      if (index !== -1) newSlots.splice(index, 1);
      else newSlots.push(slot);
      return { ...prev, timeSlot: newSlots };
    });
  };

  const handleSelectAll = (slots) => {
    setFilters(prev => {
      const alreadySelected = prev.timeSlot || [];
      const allSelected = slots.every(s => alreadySelected.includes(s));
      const updated = allSelected
        ? alreadySelected.filter(s => !slots.includes(s))
        : [...new Set([...alreadySelected, ...slots])];
      return { ...prev, timeSlot: updated };
    });
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(0);
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleFilterChange = (e) => {
      setFilters({ ...filters, [e.target.name]: e.target.value });
      setCurrentPage(0);
    };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-poppins text-driveeasy-blue dark:text-driveeasy-blue2 mb-4">Handle Bookings</h2>

      {/* Search */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="text"
          placeholder="Search by name,id's ,course.."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="p-2 border rounded-md w-80 bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200"
        />
        <select name="courseTitle" value={filters.courseTitle} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">All Courses</option>
          <option value="Complete Driving - With License">Course With License</option>
          <option value="Complete Driving - Without License">Course Without License</option>
        </select>
        {/* Time Slot Dropdown */}
        <div className="relative" ref={timeSlotRef}>
          <button
            onClick={() => setIsTimeSlotDropdownOpen(!isTimeSlotDropdownOpen)}
            className="p-2 border rounded-md w-40 flex justify-between items-center"
          >
            <span className="flex items-center gap-2">
              Time Slot {filters.timeSlot?.length > 0 && `(${filters.timeSlot.length})`}
            </span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isTimeSlotDropdownOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isTimeSlotDropdownOpen && (
            <div className="absolute z-50 bg-white dark:bg-neutral-800 border dark:border-neutral-700 mt-1 w-60 shadow-lg rounded-md max-h-64 overflow-y-auto text-sm">
              <div className="sticky top-0 bg-white dark:bg-neutral-800 z-10 p-2 border-b dark:border-neutral-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-semibold">AM Slots</span>
                  <button onClick={() => handleSelectAll(AM_SLOTS)} className="text-blue-600 text-xs underline">
                    Select All
                  </button>
                </div>
              </div>
              {AM_SLOTS.map(slot => (
                <label key={slot} className="block px-4 py-1 hover:bg-yellow-50 dark:hover:bg-neutral-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.timeSlot?.includes(slot)}
                    onChange={() => handleTimeSlotToggle(slot)}
                    className="mr-2"
                  />
                  {slot}
                </label>
              ))}

              <div className="sticky top-0 bg-white dark:bg-neutral-800 z-10 p-2 border-b dark:border-neutral-700 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-semibold">PM Slots</span>
                  <button onClick={() => handleSelectAll(PM_SLOTS)} className="text-blue-600 text-xs underline">
                    Select All
                  </button>
                </div>
              </div>
              {PM_SLOTS.map(slot => (
                <label key={slot} className="block px-4 py-1 hover:bg-blue-50 dark:hover:bg-neutral-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.timeSlot?.includes(slot)}
                    onChange={() => handleTimeSlotToggle(slot)}
                    className="mr-2"
                  />
                  {slot}
                </label>
              ))}

              {filters.timeSlot?.length > 0 && (
                <div className="sticky bottom-0 bg-white dark:bg-neutral-800 z-10 p-2 border-t dark:border-neutral-700 text-center">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, timeSlot: [] }))}
                    className="text-red-500 hover:underline text-xs"
                  >
                    Clear Filter
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <select name="license" value={filters.license} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">Select</option>
          <option value="Required">Required</option>
          <option value="Not Required">Not Required</option>
        </select>
        <select name="isTrainerLive" value={filters.isTrainerLive} onChange={handleFilterChange} className="p-2 border rounded-md bg-white dark:bg-neutral-700 dark:border-neutral-600 dark:text-gray-200">
          <option value="">Select</option>
          <option value="true">Live</option>
          <option value="false">Offline</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-neutral-800 shadow rounded-lg text-sm border border-gray-100 dark:border-neutral-800">
          <thead>
            <tr className="bg-driveeasy-blue text-white dark:bg-neutral-700 dark:text-gray-200">
              <th className="p-3 text-left">Booking ID</th>
              <th className="p-3 text-left">StudentId</th>
              <th className="p-3 text-left">Student Name</th>
              <th className="p-3 text-left">Course Title</th>
              <th className="p-3 text-left">Time Slot</th>
              <th className="p-3 text-left">Payment</th>
              <th onClick={() => handleSortChange('createdAt')}>
                On {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSortChange('startDate')}>
                From {sortField === 'startDate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSortChange('endDate')}>
                Till {sortField === 'endDate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length > 0 ? bookings.map((booking) => (
              <React.Fragment key={booking.bookingId}>
                <tr className="border-b border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700/70 text-black dark:text-gray-300">
                  <td className="p-3 text-driveeasy-blue">{booking.bookingId}</td>
                    <td
                      className="p-3 text-driveeasy-blue underline cursor-pointer"
                      onClick={() => navigate(`/users?studentId=${booking.studentId}`)}
                    >
                      {booking.studentId || 'N/A'}
                    </td>
                  <td className="p-3">{booking.studentName || 'N/A'}</td>
                  <td className="p-3">
                    {booking.courseTitle === 'Complete Driving - With License' ? 'CD - With Course' :
                     booking.courseTitle === 'Complete Driving - Without License' ? 'CD - Without Course' :
                     booking.courseTitle || 'N/A'}
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium
                      ${booking.timeSlot?.includes('AM')
                        ? 'bg-yellow-100 text-yellow-800'
                        : booking.timeSlot?.includes('PM')
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-200 text-gray-800'
                      }`}>
                      {booking.timeSlot?.includes('AM') ? (
                        <SunIcon className="h-4 w-4" />
                      ) : booking.timeSlot?.includes('PM') ? (
                        <MoonIcon className="h-4 w-4" />
                      ) : (
                        ''
                      )}
                      {booking.timeSlot || 'N/A'}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-white ${booking.paymentStatus === 'PAID' ? 'bg-red-500' : 'bg-green-500'}`}>
                      {booking.paymentStatus}
                    </span>
                  </td>
                 <td className="p-3">
                   {booking.createdAt?.seconds ? new Date(booking.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                 </td>
                  <td className="p-3">
                    {booking.startDate?.seconds ? new Date(booking.startDate.seconds * 1000).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-3">
                      {booking.endDate?.seconds ? new Date(booking.endDate.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </td>
                  <td className="p-3">
                    <button onClick={() => toggleRow(booking.bookingId)}>
                      <FaEye className={expandedRows[booking.bookingId] ? 'text-driveeasy-yellow' : 'text-driveeasy-blue'} />
                    </button>
                  </td>
                </tr>
                {expandedRows[booking.bookingId] && (
                  <tr>
                    <td colSpan="8" className="p-3 bg-gray-50 dark:bg-neutral-800 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-driveeasy-blue font-semibold">Booking Details</h4>
                          <p>Course: {booking.courseTitle}</p>
                          <p>Amount: {booking.amount}</p>
                          <p>Start Date: {booking.startDate?.seconds ? new Date(booking.startDate.seconds * 1000).toLocaleString() : 'N/A'}</p>
                          <p>End Date: {booking.endDate?.seconds ? new Date(booking.endDate.seconds * 1000).toLocaleString() : 'N/A'}</p>
                          <p>Time Slot: {booking.timeSlot || 'N/A'}</p>
                          {booking.license && <p>License: {booking.license}</p>}
                          {booking.coupon && <p>Coupon: {booking.coupon}</p>}
                          {booking.giftCardCode && <p>Gift Card Code: {booking.giftCardCode}</p>}
                        </div>
                        <div>
                          <h4 className="text-driveeasy-blue font-semibold">Attendance Details</h4>
                          <p>Present: {booking.daysPresent}</p>
                          <p>Absent: {booking.daysAbsent}</p>
                          <p>Total Days: {booking.totalDays}</p>
                          <p>Reschedules: {booking.rescheduleCount}</p>
                          <p>Trainer Live: {booking.isTrainerLive ? 'Yes' : 'No'}</p>
                        </div>
                         <div>
                           <h4 className="text-driveeasy-blue font-semibold">Student Info</h4>
                           <p>Student ID: {booking.studentId}</p>
                           <p>Age: {booking.age}</p>
                           <p>Address: {booking.address?.addressLine1 || 'N/A'}</p>
                             <p>City: {booking.address?.city || 'N/A'}</p>
                             {booking.address?.floor && <p>Floor: {booking.address?.floor || 'N/A'}</p>}
                             <p>Full Address: {booking.address?.fullAddress || 'N/A'}</p>
                             {booking.address?.landmark && <p>Landmark: {booking.address?.landmark || 'N/A'}</p>}
                             <p>Pincode: {booking.address?.pincode || 'N/A'}</p>
                             <p>State: {booking.address?.state || 'N/A'}</p>
                             <p>Location: {booking.address.geoPoint?.latitude || 'N/A'}, {booking.address.geoPoint?.longitude || 'N/A'}</p>
                        </div>
                        <div>
                           <h4 className="text-driveeasy-blue font-semibold">Car & Trainer Info</h4>
                           <p>Instructor ID: {booking.instructorId}</p>
                           <p>School Owner ID: {booking.schoolOwnerId}</p>
                           <p>Car ID: {booking.carId}</p>
                           <p>Seat ID: {booking.seatId}</p>
                         </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )) : (
              <tr>
                <td colSpan="8" className="text-center p-4">No bookings found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <ReactPaginate
        previousLabel={'Previous'}
        nextLabel={'Next'}
        pageCount={pageCount}
        onPageChange={handlePageChange}
        containerClassName={'flex justify-center mt-4 space-x-2'}
        pageClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-600 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
        activeClassName={'bg-driveeasy-blue text-white'}
        previousClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
        nextClassName={'px-3 py-1 border rounded border-gray-200 dark:border-neutral-700 hover:bg-driveeasy-blue hover:text-white dark:text-gray-100'}
      />
    </div>
  );
};

export default HandleBookings;
